import {
  CANONICAL_REVIEW_WF,
  CANONICAL_SUBMISSION_WF
} from '@publishing-engine/submission-service/src/workflow-configuration/presets/canonical-workflow.transition-graphs';
import { IDomainEvent } from '.';
import { EventPhase } from '../../../models/email-templates';
import { CANONICAL_EARLY_ASSESSMENT_WF } from '@publishing-engine/submission-service/src/workflow-configuration/presets/canonical-wf-early-assessment.transition-graphs';
import { CANONICAL_ADJUDICATION_WF } from '@publishing-engine/submission-service/src/workflow-configuration/presets/canonical-wf-adjudication.transition-graphs';

const peerReviewRequestSubtypes = [
  'manual',
  'automated',
  'manualBoardMember',
  'automaticBoardMember'
] as const;

export type IEmailPersonalizableType =
  | 'forced-on'
  | 'forced-off'
  | 'selectable';

type ConfigType = {
  // possibility of customizing the email for different phases: 'submission', 'resubmission', 'revision'
  phases: EventPhase[];

  // will the email personalization be allowed
  personalizable: IEmailPersonalizableType;
};

type EventByType = { [E in IDomainEvent as E['type']]: E };

type NonEmptyArray<T> = readonly [T, ...T[]];

type EventDetailMap = {
  [K in keyof EventByType]: (EventByType[K] extends {
    subtype: infer S;
    action: infer A;
  }
    ? { subtype: NonEmptyArray<S>; action: NonEmptyArray<A> }
    : EventByType[K] extends { subtype: infer S }
      ? { subtype: NonEmptyArray<S> }
      : EventByType[K] extends { action: infer A }
        ? { action: NonEmptyArray<A> }
        : {}) & { config: ConfigType | null };
};

export const allEmailEventsAndSubtypeOrActionMapping: EventDetailMap & {
  SignupEmail: { config: { personalizable: string } };
  ForgotPasswordEmail: { config: { personalizable: string } };
  BoardMemberReviewRequestEmail: { config: null };
  SubmittingAuthorOnlyCopyrightFormEmail: { config: null };
  SubmittingAuthorForAllCopyrightFormEmail: { config: null };
  CoAuthorForAllCopyrightUploadedEmail: { config: null };
  UserCreationByAdminEmail: { config: { personalizable: string } };
  UserUpdatedByAdminEmail: { config: { personalizable: string } };
  AccountsMergedSummaryEmail: { config: { personalizable: string } };
  EditorForAllCopyrightUploadedEmail: { config: null };
  EditorForAllICMJEUploadedEmail: { config: null };
  EditorSubmittingAuthorForAllCopyrightFormEmail: { config: null };
  EditorForSubmittingAuthorOnlyRequestCopyrightFormEmail: { config: null };
  EditorForSubmittingAuthorOnlyRequestICMJEFormEmail: { config: null };
  EditorSubmittingAuthorForAllRequestCopyrightFormEmail: { config: null };
  EditorForCoAuthorOnlyRequestCopyrightFormEmail: { config: null };
  CoAuthorOnlyCopyrightFormEmail: { config: null };
  SubmittingAuthorOnlyICMJEFormEmail: { config: null };
  SubmittingAuthorForAllICMJEFormEmail: { config: null };
  CoAuthorForAllICMJEUploadedEmail: { config: null };
  EditorSubmittingAuthorForAllICMJEFormEmail: { config: null };
  EditorSubmittingAuthorForAllRequestICMJEFormEmail: { config: null };
  SignupOnboardingProgress: { config: { personalizable: string } };
  VendorExport: { config: null };
  ReportsEmail: { config: null };
  ContactUsVerified: { config: null };
  ContactUsUnverified: { config: null };
  COCEditorAssigned: { config: { personalizable: string } };
  SecondaryEmailVerification: { config: null };
  CCEmailVerification: { config: null };
  InboundEmailFailureAlert: { config: null };
} = {
  AcceptedForFormalAcceptance: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  AdjudicationReminderTriggered: {
    config: null
  },
  AdjudicationRequestDecision: {
    subtype: ['accept', 'decline'],
    config: null
  },
  AdjudicationRequested: {
    config: null
  },
  AdjudicationSubmitted: {
    config: null
  },
  AuthorChangeRequestCompleted: {
    subtype: ['fields', 'form'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  AuthorCopyrightSubmitted: {
    subtype: ['fields', 'form'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  AuthorSubmissionEdited: {
    subtype: ['fields', 'form'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  AuthorSubmissionResubmitted: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  AuthorSubmissionWithdrawalRequestRevoked: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  AuthorSubmissionWithdrawalRequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  BoardMember: {
    action: ['added', 'removed'],
    config: null
  },
  CoReviewerInvited: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EarlyAssessmentReminderTriggered: {
    config: null
  },
  EarlyAssessmentRequestDecision: {
    subtype: ['accept', 'decline'],
    config: null
  },
  EarlyAssessmentRequested: {
    config: null
  },
  EarlyAssessmentSubmitted: {
    config: null
  },
  EditorialChangeRequestSubmitted: {
    subtype: ['fields', 'form'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialCopyrightRequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialCopyrightUploaded: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialICMJEUploaded: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialICMJERequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ContactJournal: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialFormDataCreated: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialImmediateRevisionRequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  EditorialSubmissionDecision: {
    subtype: ['accept', 'approveForPeerReview'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialSubmissionRejected: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  EditorialSubmissionUnsubmitted: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  EditorialSubmissionWithdrawalAccepted: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  EditorialSubmissionWithdrawn: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  EditorialTransferOffered: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  EnqueuedReviewerRemoved: {
    config: { phases: [], personalizable: 'forced-off' }
  },
  FirstAssessmentSubmitted: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  IntegrationResult: { config: null },
  JournalRoleAssignment: {
    action: ['created', 'updated', 'deleted'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  LicensingSubmitted: {
    subtype: ['fields', 'form'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  MinReviewCountSet: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ArticleType: {
    action: ['created'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  OrganizationCreated: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  WorkspaceMember: {
    action: ['added', 'removed'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  WorkspaceRole: {
    action: ['created', 'deleted', 'updated'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  PeerReviewDecision: {
    subtype: ['acceptRequest', 'declineRequest', 'autoAcceptRequest'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  PeerReviewModerated: {
    subtype: ['firstAssessment', 'revisionAssessment'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  PortfolioCreated: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  QueueToggled: { config: null },
  RescindedFirstAssessment: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ReviewDeadlineExtended: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ReviewDeadlineExtensionRequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ReviewDeadlinePassed: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ReviewInRevisionRequested: {
    subtype: peerReviewRequestSubtypes,
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  ReviewRated: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  ReviewReminderTriggered: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ReviewRemoved: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ReviewRequestEnqueued: {
    config: { personalizable: 'forced-off', phases: [] }
  },
  ReviewRequested: {
    subtype: peerReviewRequestSubtypes,
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  DeclinedReviewInvitationResent: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  ExpiredReviewInvitationResent: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  WithdrawnReviewInvitationResent: {
    subtype: ['assessment', 'invited'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  RevisionAssessmentSubmitted: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  RevisionRequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  RevisionReviewRequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  RevisionSubmitted: {
    subtype: ['fields', 'form'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionApprovedForPreProduction: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  SubmissionAssigned: {
    subtype: ['editorial', 'owner'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionContextCreated: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionCreated: {
    config: { phases: ['sub'], personalizable: 'forced-off' }
  },
  SubmissionCloned: {
    config: { phases: ['sub'], personalizable: 'forced-off' }
  },
  SubmissionDeadlineExtended: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionDeadlineExtensionRequested: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionDeadlinePassed: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionEdited: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  NotifyAuthorOnManuscriptUpdate: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionNote: {
    action: ['created', 'deleted', 'updated'],
    config: { personalizable: 'forced-off', phases: ['sub', 'rev', 'resub'] }
  },
  SubmissionReminderTriggered: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionTagsReplaced: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionUnassigned: {
    subtype: ['editorial', 'owner'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  TransferDeclined: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  TransferOfferAccepted: {
    subtype: ['fields', 'form'],
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  CoReviewDecision: {
    subtype: ['acceptRequest', 'declineRequest'],
    config: null
  },
  AdhocEmailSent: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-on' }
  },
  EditorialInvitedReviewInvitationWithdrawn: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialAcceptedReviewInvitationWithdrawn: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialAwaitingRevisionReviewInvitationWithdrawn: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialRevisionInvitedReviewInvitationWithdrawn: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  EditorialRevisionAssessmentReviewInvitationWithdrawn: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  SubmissionDraftCreated: {
    config: null
  },
  SubmissionAccessed: {
    config: null
  },
  SignupEmail: {
    config: { personalizable: 'forced-off' }
  },
  ForgotPasswordEmail: {
    config: { personalizable: 'forced-off' }
  },
  BoardMemberReviewRequestEmail: {
    config: null
  },
  UserCreationByAdminEmail: {
    config: { personalizable: 'forced-off' }
  },
  UserUpdatedByAdminEmail: {
    config: { personalizable: 'forced-off' }
  },
  AccountsMergedSummaryEmail: {
    config: { personalizable: 'forced-off' }
  },
  SubmittingAuthorOnlyCopyrightFormEmail: { config: null },
  SubmittingAuthorForAllCopyrightFormEmail: { config: null },
  SubmittingAuthorForAllICMJEFormEmail: { config: null },
  CoAuthorForAllCopyrightUploadedEmail: { config: null },
  EditorForAllCopyrightUploadedEmail: { config: null },
  EditorForAllICMJEUploadedEmail: { config: null },
  EditorSubmittingAuthorForAllCopyrightFormEmail: { config: null },
  EditorForSubmittingAuthorOnlyRequestCopyrightFormEmail: { config: null },
  EditorForSubmittingAuthorOnlyRequestICMJEFormEmail: { config: null },
  EditorForCoAuthorOnlyRequestCopyrightFormEmail: { config: null },
  EditorSubmittingAuthorForAllRequestCopyrightFormEmail: { config: null },
  SubmittingAuthorOnlyICMJEFormEmail: { config: null },
  CoAuthorOnlyCopyrightFormEmail: { config: null },
  CoAuthorForAllICMJEUploadedEmail: { config: null },
  EditorSubmittingAuthorForAllICMJEFormEmail: { config: null },
  EditorSubmittingAuthorForAllRequestICMJEFormEmail: { config: null },
  SubmissionDeadlineExtensionDenied: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ReviewDeadlineExtensionDenied: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  ExportPreprodSubmission: {
    action: ['complete', 'initialized', 'failed'],
    config: null
  },
  PeerReviewInterrupted: {
    config: null
  },
  ArticleTypeChanged: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  AuthorCopyrightUploaded: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  AuthorICMJEUploaded: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'selectable' }
  },
  DecisionReverted: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  AuthorCopyrightReset: { config: null },
  COCEditorAssigned: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  COCRoleUnassigned: {
    config: { phases: ['sub', 'resub', 'rev'], personalizable: 'forced-off' }
  },
  COCRecommendationSubmitted: { config: null },
  SignupOnboardingProgress: {
    config: { personalizable: 'forced-off' }
  },
  SubmissionAssignedCOC: { config: null },
  VendorExport: {
    subtype: ['complete', 'initialized', 'failed'],
    config: null
  },
  COCRecommendationModerated: { config: null },
  ReportsEmail: { config: null },
  ContactUsVerified: { config: null },
  ContactUsUnverified: { config: null },
  SecondaryEmailVerification: { config: null },
  CCEmailVerification: { config: null },
  InboundEmailFailureAlert: { config: null }
} as const;

type ExtractEventLabelKeys<T> = {
  [K in keyof T]: T[K] extends { subtype: readonly (infer S)[] }
    ? `${Extract<K, string>}#${Extract<S, string>}`
    : T[K] extends { action: readonly (infer A)[] }
      ? `${Extract<K, string>}#${Extract<A, string>}`
      : Extract<K, string>;
}[keyof T];

export type EventLabelKey = ExtractEventLabelKeys<
  typeof allEmailEventsAndSubtypeOrActionMapping
>;

type LabelType = { enabled: boolean; label: string };

type EventLabelMap = Record<EventLabelKey, LabelType>;

export type ExtendedEventLabelMap = EventLabelMap & {
  SignupEmail: LabelType;
  ForgotPasswordEmail: LabelType;
  BoardMemberReviewRequestEmail: LabelType;
  'PeerReviewDecision#autoAcceptRequest': LabelType;
  SubmittingAuthorOnlyCopyrightFormEmail: LabelType;
  SubmittingAuthorForAllCopyrightFormEmail: LabelType;
  CoAuthorForAllCopyrightUploadedEmail: LabelType;
  EditorForAllCopyrightUploadedEmail: LabelType;
  EditorForAllICMJEUploadedEmail: LabelType;
  EditorSubmittingAuthorForAllCopyrightFormEmail: LabelType;
  EditorForSubmittingAuthorOnlyRequestCopyrightFormEmail: LabelType;
  EditorForSubmittingAuthorOnlyRequestICMJEFormEmail: LabelType;
  EditorSubmittingAuthorForAllRequestCopyrightFormEmail: LabelType;
  EditorForCoAuthorOnlyRequestCopyrightFormEmail: LabelType;
  SubmittingAuthorOnlyICMJEFormEmail: LabelType;
  SubmittingAuthorForAllICMJEFormEmail: LabelType;
  CoAuthorOnlyCopyrightFormEmail: LabelType;
  CoAuthorForAllICMJEUploadedEmail: LabelType;
  EditorSubmittingAuthorForAllICMJEFormEmail: LabelType;
  EditorSubmittingAuthorForAllRequestICMJEFormEmail: LabelType;
  UserUpdatedByAdminEmail: LabelType;
  SignupOnboardingProgress: LabelType;
  ReportsEmail: LabelType;
  ContactUsVerified: LabelType;
  ContactUsUnverified: LabelType;
  COCEditorAssigned: LabelType;
  InboundEmailFailureAlert: LabelType;
};

export const eventLabel: ExtendedEventLabelMap = {
  SignupEmail: {
    label: 'Signup Mail',
    enabled: true
  },
  ForgotPasswordEmail: {
    label: 'Forgot Password Mail',
    enabled: true
  },
  BoardMemberReviewRequestEmail: {
    label: 'Board Member Review Request',
    enabled: true
  },
  SubmittingAuthorOnlyCopyrightFormEmail: {
    label: 'Submitting Author Only Copyright Form',
    enabled: true
  },
  SubmittingAuthorOnlyICMJEFormEmail: {
    label: 'Submitting Author Only Forms and Disclosures',
    enabled: true
  },
  CoAuthorForAllCopyrightUploadedEmail: {
    label: 'Co-Author For All Copyright Uploaded Form',
    enabled: true
  },
  SubmittingAuthorForAllCopyrightFormEmail: {
    label: 'Submitting Author For All Copyright Form',
    enabled: true
  },
  UserCreationByAdminEmail: {
    label: 'Account Creation By Admin',
    enabled: true
  },
  UserUpdatedByAdminEmail: {
    label: 'Account Updated By Admin',
    enabled: true
  },
  AccountsMergedSummaryEmail: {
    label: 'Account Merge Summary',
    enabled: true
  },
  EditorSubmittingAuthorForAllCopyrightFormEmail: {
    label: 'Editor Submitting Author For All Copyright Form',
    enabled: true
  },
  EditorForSubmittingAuthorOnlyRequestCopyrightFormEmail: {
    label: 'Editor For Submitting Author Only Request Copyright Form',
    enabled: true
  },
  EditorForSubmittingAuthorOnlyRequestICMJEFormEmail: {
    label: 'Editor For Submitting Author Only Request Forms and Disclosures',
    enabled: true
  },
  EditorSubmittingAuthorForAllRequestCopyrightFormEmail: {
    label: 'Editor Submitting Author For All Request Copyright Form',
    enabled: true
  },
  EditorForAllCopyrightUploadedEmail: {
    label: 'Editor For All Copyright Uploaded Form',
    enabled: true
  },
  EditorForAllICMJEUploadedEmail: {
    label: 'Editor For All Forms and Disclosures Uploaded Form',
    enabled: true
  },
  EditorForCoAuthorOnlyRequestCopyrightFormEmail: {
    label: 'Editor For CoAuthor Only Request Copyright Form',
    enabled: true
  },
  CoAuthorOnlyCopyrightFormEmail: {
    label: 'Co-Author Only Copyright Form',
    enabled: true
  },
  SubmittingAuthorForAllICMJEFormEmail: {
    label: 'Submitting Author For All Forms and Disclosures',
    enabled: true
  },
  CoAuthorForAllICMJEUploadedEmail: {
    label: 'Co-Author For All Forms and Disclosures Uploaded Form',
    enabled: true
  },
  EditorSubmittingAuthorForAllICMJEFormEmail: {
    label: 'Editor Submitting Author For All Forms and Disclosures',
    enabled: true
  },
  EditorSubmittingAuthorForAllRequestICMJEFormEmail: {
    label: 'Editor For Submitting Author For All Request Forms and Disclosures',
    enabled: true
  },
  AcceptedForFormalAcceptance: {
    label: 'Submission is Formally Accepted',
    enabled: true
  },
  AdhocEmailSent: { label: 'New Email', enabled: true },
  AdjudicationReminderTriggered: {
    label: 'Adjudication Reminders',
    enabled: false
  },
  'AdjudicationRequestDecision#accept': {
    label: 'Accept Request to Adjudicate Reviews',
    enabled: false
  },
  'AdjudicationRequestDecision#decline': {
    label: 'Decline Request to Adjudicate Reviews',
    enabled: false
  },
  AdjudicationRequested: {
    label: 'Editor Requests an Adjudication from a Reviewer/Board Member',
    enabled: false
  },
  AdjudicationSubmitted: {
    label: 'Reviewer/Board Member Submits an Adjudication',
    enabled: false
  },
  'AuthorChangeRequestCompleted#fields': {
    label: 'Author Completes an Individual Change Request',
    enabled: true
  },
  'AuthorChangeRequestCompleted#form': {
    label: 'Author Completes Change Requests, Submits Info to Editor',
    enabled: true
  },
  'AuthorCopyrightSubmitted#form': {
    label: 'Author Submits a Copyright Agreement',
    enabled: true
  },
  'AuthorSubmissionEdited#fields': { label: 'N/A', enabled: false },
  'AuthorSubmissionEdited#form': { label: 'N/A', enabled: false },
  AuthorSubmissionResubmitted: {
    label: 'Author Resubmits their Submission',
    enabled: true
  },
  AuthorSubmissionWithdrawalRequested: {
    label: 'Author Requests to Withdraw their Submission',
    enabled: true
  },
  AuthorSubmissionWithdrawalRequestRevoked: {
    label: 'Author Rescinds their Withdrawal Request',
    enabled: true
  },
  'BoardMember#added': {
    label: 'Board Member Added to Journal',
    enabled: false
  },
  'BoardMember#removed': {
    label: 'Board Member Removed from Journal',
    enabled: false
  },
  'CoReviewDecision#acceptRequest': {
    label: 'Accept Request to Co-Review a Submission',
    enabled: true
  },
  'CoReviewDecision#declineRequest': {
    label: 'Decline Request to Co-Review a Submission',
    enabled: true
  },
  CoReviewerInvited: { label: 'Reviewer Invites Co-Reviewer', enabled: true },
  DeclinedReviewInvitationResent: {
    label: 'Editor Resends a Review Invitation (for Declined Invitations)',
    enabled: true
  },
  EarlyAssessmentReminderTriggered: {
    label: 'Early Assessment Reminders',
    enabled: false
  },
  'EarlyAssessmentRequestDecision#accept': {
    label: 'Accept Request to Perform Early Assessment',
    enabled: false
  },
  'EarlyAssessmentRequestDecision#decline': {
    label: 'Decline Request to Perform Early Assessment',
    enabled: false
  },
  EarlyAssessmentRequested: {
    label: 'Editor Requests an Early Assessment from a Board Member',
    enabled: false
  },
  EarlyAssessmentSubmitted: {
    label: 'Board Member Submitted an Early Assessment',
    enabled: false
  },
  EditorialAcceptedReviewInvitationWithdrawn: {
    label: 'Editor Withdraws Active Reviewer',
    enabled: true
  },
  'EditorialChangeRequestSubmitted#fields': {
    label: 'Editor Adds a Change Request to a Batch',
    enabled: false
  },
  'EditorialChangeRequestSubmitted#form': {
    label: 'Editor Submits Change Request Batch to the Author',
    enabled: true
  },
  EditorialCopyrightRequested: {
    label: 'Editor Requests Copyright Agreement from Author',
    enabled: true
  },
  EditorialCopyrightUploaded: {
    label: 'Editor Uploads Copyright Agreement on behalf of Authors',
    enabled: true
  },
  EditorialICMJEUploaded: {
    label: 'Editor Uploads Forms and Disclosures on behalf of Authors',
    enabled: true
  },
  EditorialICMJERequested: {
    label: 'Editor Requests Forms and Disclosures Form from Author',
    enabled: true
  },
  ContactJournal: {
    label: 'Contact Journal',
    enabled: true
  },
  EditorialFormDataCreated: { label: 'N/A', enabled: false },
  EditorialImmediateRevisionRequested: {
    label: 'Editor Invites an Author for Immediate Revision',
    enabled: true
  },
  EditorialInvitedReviewInvitationWithdrawn: {
    label: 'Editor Withdraws Invited Reviewer',
    enabled: true
  },
  'EditorialSubmissionDecision#accept': {
    label: 'Submission Moves to Initial Assessment',
    enabled: true
  },
  'EditorialSubmissionDecision#approveForPeerReview': {
    label: 'Submission Is Approved for Peer Review',
    enabled: true
  },
  EditorialSubmissionRejected: {
    label: 'Editor Rejects a Submission',
    enabled: true
  },
  EditorialSubmissionUnsubmitted: {
    label: 'Editor Unsubmits an Author’s Submission',
    enabled: true
  },
  EditorialSubmissionWithdrawalAccepted: {
    label: 'Editor Accepts Author’s Withdrawal Request',
    enabled: true
  },
  EditorialSubmissionWithdrawn: {
    label: 'Editor Withdraws Author’s Submission',
    enabled: true
  },
  EditorialTransferOffered: {
    label: 'Editor Offers to Transfer an Author’s Submission',
    enabled: true
  },
  EnqueuedReviewerRemoved: {
    label: 'Editor Removes a Reviewer from the Queue',
    enabled: true
  },
  ExpiredReviewInvitationResent: {
    label: 'Editor Resends a Review Invitation (for Expired Invitations)',
    enabled: true
  },
  FirstAssessmentSubmitted: {
    label: 'Reviewer Submits their First Review',
    enabled: true
  },
  IntegrationResult: {
    label: 'Third-Party Integration Interacts with Submission',
    enabled: true
  },
  'JournalRoleAssignment#created': {
    label: 'Admin assigns a role to a user in a Journal',
    enabled: true
  },
  'JournalRoleAssignment#deleted': {
    label: 'Admin removes the role of a user in a Journal',
    enabled: true
  },
  'JournalRoleAssignment#updated': {
    label: 'Admin edits the role of a user in a Journal',
    enabled: true
  },
  'LicensingSubmitted#fields': { label: 'N/A', enabled: false },
  'LicensingSubmitted#form': {
    label: 'Editor Changes a Licensing&Copyright Section',
    enabled: true
  },
  MinReviewCountSet: {
    label: 'Editor Adjusts a Submission’s Minimum Number of Required Reviews',
    enabled: true
  },
  'ArticleType#created': {
    label: 'Admin Creates an Article Type',
    enabled: true
  },
  'AuthorCopyrightSubmitted#fields': { label: 'N/A', enabled: false },
  OrganizationCreated: { label: 'N/A', enabled: false },
  'WorkspaceMember#added': {
    label: 'New Workspace Member Added',
    enabled: true
  },
  'WorkspaceMember#removed': {
    label: 'Workspace Member Deleted',
    enabled: true
  },
  'WorkspaceRole#created': { label: 'New User Role Created', enabled: true },
  'WorkspaceRole#deleted': { label: 'User Role Deleted', enabled: true },
  'WorkspaceRole#updated': { label: 'User Role Updated', enabled: true },
  'PeerReviewDecision#acceptRequest': {
    label: 'Accept Request to Review a Submission',
    enabled: true
  },
  'PeerReviewDecision#declineRequest': {
    label: 'Decline Request to Review a Submission',
    enabled: true
  },
  'PeerReviewModerated#firstAssessment': {
    label: 'Editor Moderates Reviewer Feedback in First Assessment',
    enabled: true
  },
  'PeerReviewModerated#revisionAssessment': {
    label: 'Editor Moderates Reviewer Feedback in Revision Assessment',
    enabled: true
  },
  PortfolioCreated: { label: 'N/A', enabled: false },
  QueueToggled: { label: 'N/A', enabled: false },
  RescindedFirstAssessment: {
    label: 'Editor Rescinds a Review',
    enabled: true
  },
  ReviewDeadlineExtended: {
    label: 'Editor Extends a Review Deadline',
    enabled: true
  },
  ReviewDeadlineExtensionRequested: {
    label: 'Reviewer Requests a Review Deadline Extension',
    enabled: true
  },
  ReviewDeadlinePassed: { label: 'Review Deadline is Overdue', enabled: true },
  'ReviewInRevisionRequested#manual': {
    label: 'Invite New Reviewer in Revision Assessment',
    enabled: true
  },
  'ReviewInRevisionRequested#automated': {
    label: 'Invite New Reviewer in Revision Assessment from the queue',
    enabled: true
  },
  'ReviewInRevisionRequested#manualBoardMember': {
    label: 'Invite Board Member as Reviewer in Revision Assessment (Manual)',
    enabled: true
  },
  'ReviewInRevisionRequested#automaticBoardMember': {
    label:
      'Invite Board Member as Reviewer in Revision Assessment (Auto-Accept)',
    enabled: true
  },
  'PeerReviewDecision#autoAcceptRequest': {
    label: 'Auto-Accept Review Invitation',
    enabled: true
  },
  ReviewRated: { label: 'Editor Rates a Review', enabled: true },
  ReviewReminderTriggered: { label: 'Peer Review Reminders', enabled: true },
  ReviewRemoved: { label: 'N/A', enabled: false },
  'ReviewRequested#manual': {
    label: 'Editor Requests a Review from a Reviewer',
    enabled: true
  },
  'ReviewRequested#automated': {
    label: 'Request Review from a Reviewer in the queue',
    enabled: true
  },
  'ReviewRequested#manualBoardMember': {
    label: 'Editor Requests a Review from a Board Member (Manual)',
    enabled: true
  },
  'ReviewRequested#automaticBoardMember': {
    label: 'Editor Requests a Review from a Board Member (Auto-Accept)',
    enabled: true
  },
  ReviewRequestEnqueued: { label: 'Reviewer Added to Queue', enabled: true },
  RevisionAssessmentSubmitted: {
    label: 'Reviewer Submits a Revision in Revision Assessment',
    enabled: true
  },
  RevisionRequested: {
    label: 'Editor Request a Revision from Author',
    enabled: true
  },
  RevisionReviewRequested: {
    label: 'Requesting a Review on a Revised Manuscript',
    enabled: true
  },
  'RevisionSubmitted#fields': { label: 'N/A', enabled: false },
  'RevisionSubmitted#form': {
    label: 'Author Submitted a Revision',
    enabled: true
  },
  SubmissionApprovedForPreProduction: {
    label: 'Submission is Provisionally Accepted (Pre-Production)',
    enabled: true
  },
  'SubmissionAssigned#editorial': {
    label: 'Editorial Team Member is Assigned to a Submission as a Supporter',
    enabled: true
  },
  'SubmissionAssigned#owner': {
    label: 'Editorial Team Member is Assigned to a Submission as an Owner',
    enabled: true
  },
  SubmissionContextCreated: { label: 'N/A', enabled: false },
  SubmissionCreated: {
    label: 'Manuscript is Submitted by Author',
    enabled: true
  },
  SubmissionCloned: {
    label: 'Submission Cloned',
    enabled: true
  },
  SubmissionDeadlineExtended: {
    label: 'Author Receives an Extension from the Editors',
    enabled: true
  },
  SubmissionDeadlineExtensionRequested: {
    label: 'Author Requests an Extension from the Editors',
    enabled: true
  },
  SubmissionDeadlinePassed: {
    label: 'Transfer Offer Deadline is Overdue',
    enabled: true
  },
  SubmissionDraftCreated: { label: 'N/A', enabled: false },
  SubmissionAccessed: { label: 'N/A', enabled: false },
  SubmissionEdited: { label: 'Editor Edited a Submission', enabled: false },
  NotifyAuthorOnManuscriptUpdate: {
    label: 'Notify Author on Manuscript Update',
    enabled: true
  },
  'SubmissionNote#created': { label: 'Submission Note Created', enabled: true },
  'SubmissionNote#deleted': { label: 'Submission Note Deleted', enabled: true },
  'SubmissionNote#updated': { label: 'Submission Note Edited', enabled: true },
  SubmissionReminderTriggered: { label: 'Submission Reminders', enabled: true },
  SubmissionTagsReplaced: {
    label: 'Editor Edits Submission Tags',
    enabled: true
  },
  'SubmissionUnassigned#editorial': {
    label: 'Editorial Team Member is Unassigned from a Submission',
    enabled: true
  },
  'SubmissionUnassigned#owner': {
    label: 'Submission Owner is Unassigned from a Submission',
    enabled: true
  },
  TransferDeclined: { label: 'Transfer Offer Was Declined', enabled: true },
  'TransferOfferAccepted#fields': {
    label: 'Transfer Offer Accepted (Requires Data)',
    enabled: true
  },
  'TransferOfferAccepted#form': {
    label: 'Transfer Offer Accepted (Normal)',
    enabled: true
  },
  'WithdrawnReviewInvitationResent#assessment': {
    label:
      'Editor Resends a Review Invitation (for Withdrawn Invitations)-Assessment',
    enabled: true
  },
  'WithdrawnReviewInvitationResent#invited': {
    label:
      'Editor Resends a Review Invitation (for Withdrawn Invitations)-Invited',
    enabled: true
  },
  SubmissionDeadlineExtensionDenied: {
    label: 'Editor Declines a Submission Deadline Extension',
    enabled: true
  },
  ReviewDeadlineExtensionDenied: {
    label: 'Editor Declines a Review Deadline Extension',
    enabled: true
  },
  EditorialAwaitingRevisionReviewInvitationWithdrawn: {
    enabled: true,
    label: 'Editor Withdraws Reviewer (Reinvitation Pending)'
  },
  EditorialRevisionInvitedReviewInvitationWithdrawn: {
    enabled: true,
    label: 'Editor Withdraws Invited Reviewer (Revision Assessment)'
  },
  EditorialRevisionAssessmentReviewInvitationWithdrawn: {
    enabled: true,
    label: 'Editor Withdraws Active Reviewer (Revision Assessment)'
  },
  'ExportPreprodSubmission#initialized': {
    label: 'Export Preprod Submission Initialized',
    enabled: false
  },
  'ExportPreprodSubmission#complete': {
    label: 'Export Preprod Submission Complete',
    enabled: false
  },
  'ExportPreprodSubmission#failed': {
    label: 'Export Preprod Submission Failed',
    enabled: false
  },
  PeerReviewInterrupted: {
    enabled: false,
    label: ''
  },
  ArticleTypeChanged: {
    label: 'Editor changes the Article type of a submission',
    enabled: true
  },
  AuthorCopyrightUploaded: {
    label: 'Author uploaded the copyright form',
    enabled: true
  },
  AuthorICMJEUploaded: {
    label: 'Author Uploaded Forms and Disclosures',
    enabled: true
  },
  DecisionReverted: {
    label: 'Editor Reverts Submission Decision',
    enabled: true
  },
  AuthorCopyrightReset: {
    label: 'Author Copyright Form Reset',
    enabled: false
  },
  COCEditorAssigned: {
    label: 'Editor assigned to submission',
    enabled: true
  },
  COCRoleUnassigned: {
    label: 'Editorial role unassigned',
    enabled: true
  },
  COCRecommendationSubmitted: {
    label: 'Editor Recommendation Submitted',
    enabled: false
  },
  SignupOnboardingProgress: {
    label: 'Signup Onboarding Progress',
    enabled: true
  },
  SubmissionAssignedCOC: {
    label: 'Submission assigned to admin',
    enabled: true
  },
  'VendorExport#initialized': {
    label: 'Vendor Export Initialized',
    enabled: true
  },
  'VendorExport#complete': {
    label: 'Vendor Export Complete',
    enabled: true
  },
  'VendorExport#failed': {
    label: 'Vendor Export Failed',
    enabled: true
  },
  COCRecommendationModerated: {
    label: 'COC Recommendation Moderated',
    enabled: false
  },
  ReportsEmail: {
    label: 'Reports',
    enabled: true
  },
  ContactUsVerified: {
    label: 'Contact Us Verified',
    enabled: true
  },
  ContactUsUnverified: {
    label: 'Contact Us Unverified',
    enabled: true
  },
  SecondaryEmailVerification: {
    label: 'Secondary Email Verification',
    enabled: true
  },
  CCEmailVerification: {
    label: 'CC Email Verification',
    enabled: true
  },
  InboundEmailFailureAlert: {
    label: 'Inbound Email Failure Alert',
    enabled: true
  }
};

type SubmissoinStateNames = keyof (typeof CANONICAL_SUBMISSION_WF)['states'];
type ReviewStateNames = keyof (typeof CANONICAL_REVIEW_WF)['states'];
type EarlyAssessmentStateNames =
  keyof (typeof CANONICAL_EARLY_ASSESSMENT_WF)['states'];
type AdjudicationStateNames =
  keyof (typeof CANONICAL_ADJUDICATION_WF)['states'];

type ReminderEmailLabelConfigs = {
  submission: Record<SubmissoinStateNames, LabelType>;
  review: Record<ReviewStateNames, LabelType>;
  earlyAssessment: Record<EarlyAssessmentStateNames, LabelType>;
  adjudication: Record<AdjudicationStateNames, LabelType>;
};

export const EventAndEntityNameMap = {
  AdjudicationReminderTriggered: 'adjudication',
  EarlyAssessmentReminderTriggered: 'earlyAssessment',
  ReviewReminderTriggered: 'review',
  SubmissionReminderTriggered: 'submission'
} as const;

// These labels are defined by Product and Design. Please confirm before adding new
export const reminderPhaseLabels: ReminderEmailLabelConfigs = {
  submission: {
    formalAcceptance: {
      label: '',
      enabled: false
    },
    immediateRevision: {
      label: 'Finish Immediate Revision',
      enabled: true
    },
    revision: {
      label: 'Revise Your Submission',
      enabled: true
    },
    rejected: {
      label: '',
      enabled: false
    },
    withdrawn: {
      label: '',
      enabled: false
    },
    initial: {
      label: '',
      enabled: false
    },
    unsubmitted: {
      label: 'Review Submission Feedback and Resubmit',
      enabled: false
    },
    transferPending: {
      label: 'Decide on Transfer Offer',
      enabled: true
    },
    peerReviewPending: {
      label: '',
      enabled: false
    },
    transferOffered: {
      label: 'Transfer Offer Concluded',
      enabled: true
    },
    assignment: {
      label: '',
      enabled: false
    },
    revisionAssessment: {
      label: '',
      enabled: false
    },
    revisionPeerReviewInvitation: {
      label: '',
      enabled: false
    },
    revisionPeerReviewPending: {
      label: '',
      enabled: false
    },
    revisionPeerReviewFinished: {
      label: '',
      enabled: false
    },
    peerReviewInvitation: {
      label: '',
      enabled: false
    },
    peerReviewFinished: {
      label: '',
      enabled: false
    },
    accepted: {
      label: '',
      enabled: false
    },
    preProduction: {
      label: '',
      enabled: false
    },
    initialAssessment: {
      label: '',
      enabled: false
    }
  },
  review: {
    awaitingRevisionReviewRequest: {
      label: '',
      enabled: false
    },
    submittedFirstAssessment: {
      label: '',
      enabled: false
    },
    rescinded: {
      label: 'Rework Rescinded Review',
      enabled: true
    },
    invited: {
      label: 'Decide on Review Request',
      enabled: true
    },
    removed: {
      label: '',
      enabled: false
    },
    reviewed: {
      enabled: false,
      label: 'Evaluate Completed Review'
    },
    submittedRevisionAssessment: {
      label: '',
      enabled: false
    },
    initial: {
      enabled: false,
      label: ''
    },
    expired: {
      label: '',
      enabled: false
    },
    declined: {
      label: '',
      enabled: false
    },
    closed: {
      label: '',
      enabled: false
    },
    revisionAssessment: {
      label: 'Continue Reviewing on New Review Round',
      enabled: true
    },
    firstAssessment: {
      label: 'Finish Review',
      enabled: true
    },
    awaitingRevision: {
      label: '',
      enabled: false
    },
    revisionInvited: {
      label: 'Perform A Review in a New Round',
      enabled: true
    },
    withdrawn: {
      label: '',
      enabled: false
    }
  },
  earlyAssessment: {
    invited: {
      label: 'Decide on Early Assessment Request',
      enabled: true
    },
    reviewed: {
      enabled: true,
      label: 'Evaluate Completed Early Assessment'
    },
    initial: {
      enabled: false,
      label: ''
    },
    expired: {
      enabled: false,
      label: ''
    },
    declined: {
      enabled: false,
      label: ''
    },
    interrupted: {
      enabled: false,
      label: ''
    },
    assessment: {
      enabled: true,
      label: 'Finish Early Assessment'
    }
  },
  adjudication: {
    invited: {
      label: 'Decide on Adjudication Request',
      enabled: true
    },
    reviewed: {
      label: 'Evaluate Completed Adjudication',
      enabled: true
    },
    initial: {
      label: '',
      enabled: false
    },
    expired: {
      label: '',
      enabled: false
    },
    declined: {
      label: '',
      enabled: false
    },
    interrupted: {
      label: '',
      enabled: false
    },
    assessment: {
      label: 'Perform An Adjudication in a New Round',
      enabled: true
    },
    closed: {
      label: '',
      enabled: false
    }
  }
};
